import { Box } from '@mui/material'
import { Coupon } from '@dtx-company/true-common/src/types/Coupon'
import { FIRST_MONTH_FREE } from '../constants/promoCodes'
import { Plan } from '@dtx-company/true-common/src/types/Plan'
import { PlanInterval } from '@dtx-company/true-common/src/types/PlanInterval'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { getDiscountPercentForPlanFromPromo } from '../utils/getDiscountPercentForPlanFromPromo'
import { getPercentText } from '@app/common/src/utils/getPercentText'
import { getPlanIntervalName } from '../utils/getPlanIntervalName'
import { getPlanTypeName } from '../utils/getPlanTypeName'
import { getPriceText } from '../utils/getPriceText'
import { promoAppliesToSelectedPlan } from '@app/common/src/components/PlanPurchaseExperience/utils/promoAppliesToSelectedPlan'
import { useId } from 'react'
import chroma from 'chroma-js'
import styled, { css } from 'styled-components'
import theme from '../theme'

const _ItemDotBox = styled(Box)`
  transition: background-image 1000ms linear;
  flex: 0 0 auto;
  background-image: radial-gradient(circle, transparent 55%, ${theme.colors.secondary.border} 55%);

  border-radius: 50%;
  width: 20px;
  height: 20px;

  [disabled] + & {
    background-image: radial-gradient(
      circle,
      ${theme.colors.secondary.placeholder} 35%,
      transparent 35%,
      transparent 55%,
      ${theme.colors.secondary.placeholder} 55%
    );
  }
`

const _ItemInput = styled.input`
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
`

const _ItemNameText = styled(Text)`
  color: ${theme.colors.masterWeb.flowblue2};
  font-weight: 900;
  font-style: italic;
  font-size: 17px;
`

const _ItemNameIntervalText = styled(Text)`
  color: inherit;
  font-family: inherit;
  font-weight: 500;
  font-style: inherit;
  font-size: inherit;
`

const _ItemTopLineBox = styled(Box)`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  min-height: 24px;
`

const _ItemPriceText = styled(Text)`
  display: inline;
`

const _ItemPriceIntervalText = styled(Text)`
  display: inline;
`

const _ItemBottomLineBox = styled(Box)`
  display: block;
  margin-top: 4px;
`

const itemBadgeBoxBaseStyles = `
  display: block;
  border-radius: 12px;
  padding: 6px 12px;
`

const _ItemBadgeBoxV1 = styled(Box)`
  ${itemBadgeBoxBaseStyles}
  background-color: ${() => theme.colors.status.successDark};
`
const _ItemBadgeBoxV2 = styled(Box)`
  ${itemBadgeBoxBaseStyles}
  background-color: transparent;
`

const itemBadgeTextBaseStyles = `
  display: block;
  color: ${theme.colors.primary.white};
  line-height: 1;
  text-align: center;
`

const _ItemBadgeTextV1 = styled(Text)`
  ${itemBadgeTextBaseStyles}
  color: ${theme.colors.primary.white};
`

const _ItemBadgeTextV2 = styled(Text)`
  ${itemBadgeTextBaseStyles}
  color: ${theme.colors.primary.black};
`

const _ItemContentBox = styled(Box)`
  display: flex;
  flex-flow: column nowrap;
  flex: 1 1 auto;
  padding-left: 10px;
`

const _ItemLabel = styled.label<{ selected: boolean }>`
  transition: background-color, border-color 100ms linear;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  border-left: 4px solid transparent;
  border-radius: 4px;
  padding: 9px 12px;
  width: 100%;

  ${props => {
    if (props.selected) {
      return css`
        background-color: ${chroma(theme.colors.primary.flowBlue).alpha(0.05).css()};
        border-left-color: ${theme.colors.primary.flowBlue};

        ${_ItemDotBox} {
          background-image: radial-gradient(
            circle,
            ${theme.colors.primary.flowBlue} 35%,
            transparent 35%,
            transparent 55%,
            ${theme.colors.primary.flowBlue} 55%
          );
        }
      `
    }
  }}

  & + & {
    margin-top: 3px;
  }
`

const _RootBox = styled(Box)`
  display: block;
  width: 100%;
`

function getPlanTextParts(
  plan: Plan,
  discountPercentage?: number
): { priceText: string; discountedPriceText?: string; priceIntervalText: string } {
  const planPriceInCents = plan.startingPriceInCents ?? (plan.priceInCents || 0)
  const discountMultiplier = discountPercentage ? 1 - discountPercentage : 1
  if (plan.interval === PlanInterval.YEARLY) {
    const yearlyPrice = getPriceText({
      priceInCents: planPriceInCents * discountMultiplier
    })
    return {
      priceText: getPriceText({ priceInCents: planPriceInCents / 12 }),
      ...(discountPercentage && {
        discountedPriceText: getPriceText({
          priceInCents: (planPriceInCents / 12) * discountMultiplier
        })
      }),
      priceIntervalText: `per month (${yearlyPrice} billed annually)`
    }
  }
  if (plan.interval === PlanInterval.MONTHLY) {
    return {
      priceText: getPriceText({ priceInCents: planPriceInCents }),
      ...(discountPercentage && {
        discountedPriceText: getPriceText({
          priceInCents: planPriceInCents * discountMultiplier
        })
      }),
      priceIntervalText: `per month (billed monthly)`
    }
  }
  // If there's some new interval just toss all the standard boilerplate text into
  // the interval text line
  return {
    priceText: '',
    priceIntervalText: getPriceText({
      priceInCents: planPriceInCents,
      ...(discountPercentage && {
        discountedPriceText: getPriceText({
          priceInCents: planPriceInCents * discountMultiplier
        })
      }),
      interval: plan.interval,
      discountPercent: plan.discountPercent
    })
  }
}

type PlanPickerItemBadgeProps = PlanPickerItemProps

function PlanPickerItemBadge(props: PlanPickerItemBadgeProps): JSX.Element {
  let discountText
  // For `free_trial_via_locks` experiment we want to display the badges without
  // rounded borders or background just as plain text. Remove once the experiment
  // is over
  const _ItemBadgeBox = _ItemBadgeBoxV1
  const _ItemBadgeText = _ItemBadgeTextV1

  if (props.currentPlan) {
    const currentType = props.currentPlan.type
    const currentInterval = props.currentPlan.interval
    const planMatchingCurrent = props.plans.find(plan => {
      return plan.type === currentType && plan.interval === currentInterval
    })
    if (planMatchingCurrent) {
      if (props.plan.id === planMatchingCurrent.id) {
        return (
          <>
            <_ItemBadgeBox
              component="span"
              sx={{ backgroundColor: theme.colors.secondary.disabled }}
            >
              <_ItemBadgeText component="span" variant="tag/bold">
                {!props.isSinglePlan && planMatchingCurrent.discountPercent
                  ? `You are saving ${getPercentText(planMatchingCurrent.discountPercent)}`
                  : 'Current'}
              </_ItemBadgeText>
            </_ItemBadgeBox>
          </>
        )
      }
    }
  }

  // If the plan has a discount percent we want to display it in a badge
  if (props.plan.discountPercent) {
    discountText = 'Save ' + getPercentText(props.plan.discountPercent)
    return (
      <>
        <_ItemBadgeBox component="span">
          <_ItemBadgeText component="span" variant="tag/bold">
            {props.badge ? props.badge : discountText}
          </_ItemBadgeText>
        </_ItemBadgeBox>
      </>
    )
  }

  // If there is an arbitrary or overwritten badge text display it in a badge
  if (props.badge) {
    return (
      <>
        <_ItemBadgeBox component="span">
          <_ItemBadgeText component="span" variant="tag/bold">
            {props.badge}
          </_ItemBadgeText>
        </_ItemBadgeBox>
      </>
    )
  }
  return <></>
}

interface PlanPickerItemProps extends PlanPickerProps {
  guid: string
  plan: Plan
  isSinglePlan: boolean
  discountPercent?: number
  badge?: string
}

function PlanPickerItem(props: PlanPickerItemProps): JSX.Element {
  const isCurrent =
    props.currentPlan &&
    props.currentPlan.type === props.plan.type &&
    props.currentPlan.interval === props.plan.interval

  const { badge, ...rest } = props
  const discount = !isCurrent ? props?.discountPercent : undefined
  const textParts = getPlanTextParts(props.plan, discount)
  const primaryText = textParts.discountedPriceText
    ? ' ' + textParts.discountedPriceText
    : textParts.priceText
  const isSelected =
    props.selectedPlan.type === props.plan.type &&
    props.selectedPlan.interval === props.plan.interval

  const _PriceSection = (): JSX.Element | null => {
    if (textParts.discountedPriceText) {
      // If discount is of 100% display the price cross out (line-through)
      // And do not display the discounted price `primaryText`
      if (props.discountPercent == 1) {
        return (
          <>
            <_ItemPriceIntervalText
              component="span"
              variant="header/subheader"
              textDecoration={'line-through'}
            >
              {textParts.priceText}
            </_ItemPriceIntervalText>{' '}
          </>
        )
      }

      return (
        <>
          <_ItemPriceIntervalText
            component="span"
            variant="body/small"
            textDecoration={'line-through'}
          >
            {textParts.priceText}
          </_ItemPriceIntervalText>
        </>
      )
    }

    return <span>{primaryText}</span>
  }

  return (
    <_ItemLabel htmlFor={`plan_${props.plan.id}_${props.guid}`} selected={isSelected}>
      <_ItemInput
        type="radio"
        id={`plan_${props.plan.id}_${props.guid}`}
        name={`plan_${props.guid}`}
        checked={isSelected}
        disabled={isCurrent}
        onChange={() => props.onSelectedPlanChange(props.plan)}
      />
      {!props.isSinglePlan && <_ItemDotBox component="span" />}
      <_ItemContentBox component="span">
        <_ItemTopLineBox component="span">
          <_ItemNameText component="span">
            {props.plan.type && getPlanTypeName(props.plan.type)}{' '}
            <_ItemNameIntervalText component="span">
              {props.plan.interval && getPlanIntervalName(props.plan.interval)}
            </_ItemNameIntervalText>
          </_ItemNameText>{' '}
          <PlanPickerItemBadge {...rest} badge={!isCurrent ? badge : undefined} />
        </_ItemTopLineBox>
        <_ItemBottomLineBox component="span">
          <_ItemPriceText component="span" variant="header/subheader">
            <_PriceSection />{' '}
            <_ItemPriceIntervalText component="span" variant="body/small">
              {textParts.priceIntervalText}
            </_ItemPriceIntervalText>
          </_ItemPriceText>
        </_ItemBottomLineBox>
      </_ItemContentBox>
    </_ItemLabel>
  )
}

interface PlanPickerProps {
  currentPlan?: Plan
  selectedPlan: Plan
  plans: Plan[]
  onSelectedPlanChange: (value: Plan) => void
  badgeText?: string[]
  promo: Partial<Coupon>
}

/**
 * Creates a picker for selecting from a list of plans.
 *
 * Typically will receive a monthly and annual plan that complement each other
 * but can receive any number of plans.
 */
export function PlanPicker(props: PlanPickerProps): JSX.Element {
  const guid = useId()
  const isSinglePlan = props.plans.length === 1
  const { badgeText, ...rest } = props

  return (
    <>
      <_RootBox>
        {props.plans.map(plan => {
          const promoAppliesToPlan = promoAppliesToSelectedPlan(props.promo, plan)
          const promoDiscountPercent = getDiscountPercentForPlanFromPromo(props.promo, plan)

          // Display 'First month free' badge if experiment is active and variant is 'free-month',
          // the promo applies to the current plan and the promo code is FIRST_MONTH_FREE
          const badge =
            promoAppliesToPlan && props.promo.id === FIRST_MONTH_FREE ? 'First month free' : ''

          return (
            <PlanPickerItem
              key={plan.id}
              plan={plan}
              guid={guid || ''}
              isSinglePlan={isSinglePlan}
              discountPercent={promoDiscountPercent}
              badge={badge}
              {...rest}
            />
          )
        })}
      </_RootBox>
    </>
  )
}
